import React, { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import SignatureCaseRow from "./SignatureCaseRow";
import SignatureCaseEditor from "./SignatureCaseEditor";
import {
  fetchSignatureCases,
  reorderSignatureCases,
  upsertSignatureCase,
  deleteSignatureCase,
  seedSignatureCases,
} from "../../services/signatureCaseService";
import { isSupabaseConfigured } from "../../lib/supabaseClient";

export default function SignatureCaseManager() {
  const [cases, setCases] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reorderMessage, setReorderMessage] = useState("");
  const [formNotice, setFormNotice] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [hasDraftChanges, setHasDraftChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [supabaseNotice, setSupabaseNotice] = useState("");
  const [isSeeding, setIsSeeding] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor)
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const data = await fetchSignatureCases();
        const sorted = [...data].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
        setCases(sorted);
        setSelectedId((prev) => prev ?? sorted[0]?.id ?? null);
        if (isSupabaseConfigured) {
          setSupabaseNotice(sorted.length ? "Supabase bağlantısı aktif." : "Supabase tablosu boş görünüyor. Demo verilerini yüklemek için aşağıdaki butonu kullanın.");
        } else {
          setSupabaseNotice("Supabase anahtarları tanımlı değil; değişiklikler bu oturumda kalır.");
        }
      } catch (fetchError) {
        console.error(fetchError);
        setError("Signature projeleri yüklenemedi.");
        if (isSupabaseConfigured) {
          setSupabaseNotice("Supabase bağlantısında sorun oluştu. Ayarlarınızı kontrol edin.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (!selectedId && cases.length) {
      setSelectedId(cases[0].id);
    }
  }, [cases, selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setDraft(null);
      return;
    }
    const original = cases.find((item) => item.id === selectedId);
    if (original) {
      setDraft(cloneCase(original));
      setHasDraftChanges(false);
    }
  }, [cases, selectedId]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    setCases((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      const reordered = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
        ...item,
        orderIndex: index,
      }));
      queueMicrotask(() => persistReorder(reordered));
      return reordered;
    });
  };

  const persistReorder = async (nextOrder) => {
    if (!isSupabaseConfigured) {
      setReorderMessage("Sıralama yalnızca Supabase etkinleştirildiğinde kaydedilir. Şu an değişiklikler sadece ön izleme olarak duruyor.");
      return;
    }

    try {
      setReorderMessage("Sıralama kaydediliyor...");
      await reorderSignatureCases(nextOrder.map((item) => item.id));
      setReorderMessage("Sıralama güncellendi.");
    } catch (persistError) {
      console.error(persistError);
      setReorderMessage("Sıralama kaydedilemedi. Lütfen tekrar deneyin.");
    }
  };

  const handleSeedSupabase = async () => {
    if (!isSupabaseConfigured) {
      setSupabaseNotice("Supabase yapılandırması bulunamadı.");
      return;
    }

    setIsSeeding(true);
    setSupabaseNotice("Demo verileri Supabase'e aktarılıyor...");
    try {
      const seeded = await seedSignatureCases();
      setCases(seeded);
      setSelectedId((prev) => prev ?? seeded[0]?.id ?? null);
      setSupabaseNotice("Supabase demo verileri güncellendi.");
    } catch (seedError) {
      console.error(seedError);
      setSupabaseNotice("Supabase'e veri aktarılırken bir hata oluştu.");
    } finally {
      setIsSeeding(false);
    }
  };

  const infoMessage = useMemo(() => {
    if (isLoading) {
      return "Signature projeleri yükleniyor...";
    }
    if (error) {
      return error;
    }
    if (!cases.length) {
      return "Henüz kayıtlı signature proje bulunmuyor.";
    }
    return "";
  }, [isLoading, error, cases]);

  const filteredCases = useMemo(() => {
    if (!searchTerm.trim()) {
      return cases;
    }
    const query = searchTerm.trim().toLowerCase();
    return cases.filter((item) =>
      [item.title, item.slug, item.label]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query))
    );
  }, [cases, searchTerm]);

  const handleSelect = (caseId) => {
    setSelectedId(caseId);
    setFormNotice("");
  };

  const handleDraftChange = (nextDraft) => {
    setDraft(nextDraft);
    setHasDraftChanges(true);
    setFormNotice("");
  };

  const handleResetDraft = () => {
    if (!selectedId) {
      return;
    }
    const original = cases.find((item) => item.id === selectedId);
    if (original) {
      setDraft(cloneCase(original));
      setHasDraftChanges(false);
      setFormNotice("Değişiklikler sıfırlandı.");
    }
  };

  const handleCreate = () => {
    const newEntry = createBlankCase(cases.length);
    setCases((items) => {
      const next = [newEntry, ...items].map((item, index) => ({ ...item, orderIndex: index }));
      return next;
    });
    setSelectedId(newEntry.id);
    setDraft(newEntry);
    setHasDraftChanges(true);
    setFormNotice("Yeni signature proje taslağı oluşturuldu.");
  };

  const commitToState = (savedCase) => {
    setCases((items) => {
      const exists = items.some((item) => item.id === savedCase.id);
      if (exists) {
        return items.map((item) => (item.id === savedCase.id ? savedCase : item));
      }
      const next = [savedCase, ...items];
      return next.map((item, index) => ({ ...item, orderIndex: index }));
    });
    setDraft(cloneCase(savedCase));
    setSelectedId(savedCase.id);
    setHasDraftChanges(false);
  };

  const handleSaveDraft = async () => {
    if (!draft) {
      return;
    }
    setIsSaving(true);
    setFormNotice("");
    const sanitized = sanitizeCase(draft);

    try {
      if (!isSupabaseConfigured) {
        commitToState(sanitized);
        setFormNotice("Supabase tanımlı olmadığı için değişiklikler bu oturumda tutuluyor.");
        return;
      }

      const saved = await upsertSignatureCase(sanitized);
      commitToState(saved);
      setFormNotice("Signature proje kaydedildi.");
    } catch (persistError) {
      console.error(persistError);
      setFormNotice("Kaydetme sırasında bir hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCase = async (caseId) => {
    if (!caseId) {
      return;
    }

    try {
      if (isSupabaseConfigured) {
        await deleteSignatureCase(caseId);
      }
      setCases((items) => items.filter((item) => item.id !== caseId));
      setFormNotice(isSupabaseConfigured ? "Signature proje silindi." : "Taslak silindi (Supabase bağlı değil).");

      if (selectedId === caseId) {
        const fallback = cases.find((item) => item.id !== caseId);
        setSelectedId(fallback?.id ?? null);
        setDraft(fallback ? cloneCase(fallback) : null);
        setHasDraftChanges(false);
      }
    } catch (deletionError) {
      console.error(deletionError);
      setFormNotice("Silme sırasında bir hata oluştu.");
    }
  };

  const handleDuplicate = () => {
    if (!draft) {
      return;
    }
    const duplicated = cloneCase(draft);
    duplicated.id = generateId();
    duplicated.slug = `${duplicated.slug || "signature-case"}-${Math.random().toString(36).slice(2, 6)}`;
    duplicated.title = `${duplicated.title || "Yeni Signature Case"} (Kopya)`;
    duplicated.published = false;
    duplicated.orderIndex = 0;

    setCases((items) => {
      const next = [duplicated, ...items].map((item, index) => ({ ...item, orderIndex: index }));
      return next;
    });
    setSelectedId(duplicated.id);
    setDraft(duplicated);
    setHasDraftChanges(true);
    setFormNotice("Taslak kopyalandı. Kaydetmek için değişiklikleri kaydet butonunu kullanın.");
  };

  const handleTogglePublished = async (caseId) => {
    const target = cases.find((item) => item.id === caseId);
    if (!target) {
      return;
    }
    const nextState = { ...target, published: !target.published };

    if (!isSupabaseConfigured) {
      commitToState(nextState);
      setFormNotice("Supabase bağlı olmadığı için yayın durumu bu oturumda değiştirildi.");
      return;
    }

    setIsSaving(true);
    try {
      const saved = await upsertSignatureCase(nextState);
      commitToState(saved);
      setFormNotice(saved.published ? "Proje yayına alındı." : "Proje taslak olarak işaretlendi.");
    } catch (toggleError) {
      console.error(toggleError);
      setFormNotice("Yayın durumu güncellenemedi.");
    } finally {
      setIsSaving(false);
    }
  };

  const isFiltering = Boolean(searchTerm.trim());
  const listForRender = filteredCases;

  return (
    <section className="signature-manager">
      <header className="signature-manager__toolbar">
        <div>
          <h2 className="admin-shell__title" style={{ fontSize: "1.4rem" }}>Signature Portfolyo Yönetimi</h2>
          <p className="admin-shell__subtitle" style={{ maxWidth: "760px" }}>
            Solda signature projeleri sıralayıp seçin, sağdaki panelde içeriği düzenleyin. Supabase anahtarı eklediğinizde kaydetme ve yayınlama operasyonları gerçek veritabanına yazılır.
          </p>
        </div>
        <div className="signature-manager__actions">
          {isSupabaseConfigured ? (
            <button
              type="button"
              className="signature-manager__button signature-manager__button--ghost"
              onClick={handleSeedSupabase}
              disabled={isSeeding}
            >
              {isSeeding ? "Supabase senkronize ediliyor..." : "Supabase verisini senkronize et"}
            </button>
          ) : (
            <span className="signature-manager__status">Supabase pasif</span>
          )}
          <div className="signature-manager__search">
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Projelerde ara..."
            />
          </div>
          <button type="button" className="signature-manager__button" onClick={handleCreate}>
            Yeni proje
          </button>
        </div>
      </header>

      {infoMessage && <p style={{ color: "rgba(224, 229, 245, 0.68)" }}>{infoMessage}</p>}

      {!!reorderMessage && (
        <p style={{ color: "rgba(200, 167, 232, 0.75)", fontSize: "0.85rem", letterSpacing: "0.18rem" }}>{reorderMessage}</p>
      )}

      {!!supabaseNotice && (
        <p className="signature-manager__flash" style={{ borderColor: "rgba(200, 167, 232, 0.35)" }}>{supabaseNotice}</p>
      )}

      <div className="signature-manager__workspace">
        <div className="signature-manager__catalog" aria-label="Signature proje listesi">
          {isFiltering && !listForRender.length ? (
            <div className="signature-manager__empty">Arama sonucunda proje bulunamadı.</div>
          ) : null}

          {isFiltering ? (
            <div className="signature-manager__list">
              {listForRender.map((item) => (
                <SignatureCaseRow
                  key={item.id}
                  caseItem={item}
                  isActive={item.id === selectedId}
                  onSelect={handleSelect}
                  onTogglePublished={handleTogglePublished}
                  sortableDisabled
                />
              ))}
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
              <SortableContext items={cases.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                <div className="signature-manager__list">
                  {cases.map((item) => (
                    <SignatureCaseRow
                      key={item.id}
                      caseItem={item}
                      isActive={item.id === selectedId}
                      onSelect={handleSelect}
                      onTogglePublished={handleTogglePublished}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        <SignatureCaseEditor
          draft={draft}
          onChange={handleDraftChange}
          onSave={handleSaveDraft}
          onReset={handleResetDraft}
          onDelete={handleDeleteCase}
          onDuplicate={handleDuplicate}
          saving={isSaving}
          hasChanges={hasDraftChanges}
          supabaseEnabled={isSupabaseConfigured}
        />
      </div>

      {!!formNotice && (
        <p className="signature-manager__flash">{formNotice}</p>
      )}
    </section>
  );
}

const cloneCase = (payload) => JSON.parse(JSON.stringify(payload));

const generateId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `case-${Math.random().toString(36).slice(2, 10)}`;
};

const createBlankCase = (orderIndex) => ({
  id: generateId(),
  slug: "",
  label: "Signature Proje",
  title: "",
  intro: "",
  meta: ["Konum: ", "Ölçek: "],
  body: [""],
  timeline: [{ title: "", description: "" }],
  gallery: [{ src: "", alt: "" }],
  ctaLabel: "Detaylı case talep et",
  ctaUrl: "mailto:studio@modelerman.com",
  orderIndex,
  published: false,
});

const sanitizeCase = (payload) => {
  const next = cloneCase(payload);
  next.meta = (next.meta ?? []).filter((entry) => entry?.trim());
  next.body = (next.body ?? []).filter((entry) => entry?.trim());
  next.timeline = (next.timeline ?? []).filter((step) => (step.title || step.description));
  next.gallery = (next.gallery ?? []).filter((image) => image.src);
  return next;
};
